import * as Sentry from '@sentry/nextjs'

import type { BasketItem } from '@/context/basketContext'

import {
  CHECKOUT_AU,
  CHECKOUT_GB,
  CHECKOUT_NZ,
  CHECKOUT_US,
  type CheckoutCountry,
  type CurrencyCode,
} from '@/lib/checkout/types'

import { PriceProductType, type Price } from '@/clients/payment-service'

export type GutHealthBarFlavour = 'BERRY' | 'CHOCO'
export type GutHealthBarPaymentType = 'ONE_TIME' | '1_MONTH'

export const GUT_HEALTH_BAR_CONFIG = {
  flavours: ['BERRY', 'CHOCO'] as const satisfies GutHealthBarFlavour[],
  paymentTypes: ['ONE_TIME', '1_MONTH'] as const satisfies GutHealthBarPaymentType[],
  maxQuantityPerOrder: 4,
  barsPerBox: 12,
} as const

type GutHealthBarShipping = {
  // Free delivery once the items subtotal reaches this threshold (in minor
  // units, £40). Mirrors payment-service `SHIPPING_FEE_THRESHOLD`; below it the
  // postage fee applies.
  freeShippingThreshold: number
  feeLookupKey: string
  defaultFee: number
}

export type GutHealthBarCountryConfig = {
  minQuantityPerOrder: number
  shipping: GutHealthBarShipping | null
}

const DEFAULT_COUNTRY_CONFIG: GutHealthBarCountryConfig = {
  minQuantityPerOrder: 1,
  shipping: {
    freeShippingThreshold: 4000,
    feeLookupKey: 'GB_SHIPPING_FEE_4_ONE_TIME',
    defaultFee: 400,
  },
}

const GUT_HEALTH_BAR_COUNTRY_CONFIG: Record<CheckoutCountry, GutHealthBarCountryConfig> = {
  [CHECKOUT_GB]: DEFAULT_COUNTRY_CONFIG,
  [CHECKOUT_US]: { ...DEFAULT_COUNTRY_CONFIG, shipping: null },
  [CHECKOUT_AU]: { ...DEFAULT_COUNTRY_CONFIG, minQuantityPerOrder: 2, shipping: null },
  [CHECKOUT_NZ]: { ...DEFAULT_COUNTRY_CONFIG, minQuantityPerOrder: 2, shipping: null },
}

export const getGutHealthBarCountryConfig = (country: CheckoutCountry): GutHealthBarCountryConfig =>
  GUT_HEALTH_BAR_COUNTRY_CONFIG[country]

export const isGutHealthBarFlavour = (value: string | null | undefined): value is GutHealthBarFlavour =>
  !!value && (GUT_HEALTH_BAR_CONFIG.flavours as readonly string[]).includes(value)

export const isGutHealthBarPaymentType = (value: string | null | undefined): value is GutHealthBarPaymentType =>
  !!value && (GUT_HEALTH_BAR_CONFIG.paymentTypes as readonly string[]).includes(value)

export const derivePaymentType = (price: Price): GutHealthBarPaymentType | undefined => {
  if (!price.recurring) return 'ONE_TIME'
  if (price.recurring.interval?.toLowerCase() === 'month' && price.recurring.interval_count === 1) return '1_MONTH'
  Sentry.captureException(new Error('Unsupported recurring shape on gut health bar price'), {
    extra: { lookupKey: price.lookup_key, recurring: price.recurring },
  })
  return undefined
}

export const getFlavourFromLookupKey = (lookupKey: string): GutHealthBarFlavour | undefined =>
  GUT_HEALTH_BAR_CONFIG.flavours.find((flavour) => new RegExp(`_${flavour}_\\d+_BARS_`).test(lookupKey))

/**
 * Flavour and cadence alone do not identify a price. The GB Stripe account also
 * carries AU and NZ prices whose lookup keys match the same shape (for example
 * `NZ_BERRY_12_BARS_1_MONTH_TEST`), so the currency has to be matched too or
 * `find` returns whichever Stripe listed first. See ECO-3476.
 */
export const findVariantPrice = (
  prices: Price[],
  flavour: GutHealthBarFlavour,
  paymentType: GutHealthBarPaymentType,
  currency: CurrencyCode,
): Price | undefined =>
  prices.find(
    (price) =>
      price.currency === currency &&
      price.product_type === PriceProductType.BARS &&
      getFlavourFromLookupKey(price.lookup_key) === flavour &&
      derivePaymentType(price) === paymentType,
  )

export const findVariantLookupKey = (
  prices: Price[],
  flavour: GutHealthBarFlavour,
  paymentType: GutHealthBarPaymentType,
  currency: CurrencyCode,
): string | undefined => findVariantPrice(prices, flavour, paymentType, currency)?.lookup_key

export const clampQuantitiesToCap = (
  requestedQuantities: Record<GutHealthBarFlavour, number>,
  cap: number,
): Record<GutHealthBarFlavour, number> => {
  let remaining = cap
  const clamped = {} as Record<GutHealthBarFlavour, number>
  GUT_HEALTH_BAR_CONFIG.flavours.forEach((flavour) => {
    const quantity = Math.max(0, Math.min(requestedQuantities[flavour] ?? 0, remaining))
    clamped[flavour] = quantity
    remaining -= quantity
  })
  return clamped
}

export const buildBasketItems = (
  prices: Price[],
  quantities: Record<GutHealthBarFlavour, number>,
  paymentType: GutHealthBarPaymentType,
  currency: CurrencyCode,
): BasketItem[] =>
  GUT_HEALTH_BAR_CONFIG.flavours
    .map((flavour): BasketItem | null => {
      const qty = quantities[flavour]
      if (qty <= 0) return null
      const lookupKey = findVariantLookupKey(prices, flavour, paymentType, currency)
      return lookupKey ? { priceLookupKey: lookupKey, quantity: qty } : null
    })
    .filter((item): item is BasketItem => item !== null)

interface BarsPrices {
  pricePerBox: number
  pricePerBar: number
}

// By design every flavour shares the same price — only the payment type
// (one-off vs monthly) changes it. So prices are keyed by payment type alone,
// not per flavour. The per-flavour Stripe lookup keys still differ (see
// `findVariantLookupKey`); only the displayed price is shared.
export type BarsPricesByPaymentType = Record<GutHealthBarPaymentType, BarsPrices>

export interface PDPPricingSummary {
  oneTimeSubtotal: number
  subscriptionSubtotal: number
  savings: number
  totalBars: number
}

/**
 * Resolves the box/bar price for a payment mode. By design all flavours share
 * the same price, so we read the first matching BARS variant for the mode
 * rather than tracking a price per flavour.
 */
export const buildBarsPrices = (
  prices: Price[],
  paymentType: GutHealthBarPaymentType,
  currency: CurrencyCode,
): BarsPrices => {
  const variant = prices.find(
    (price) =>
      price.currency === currency &&
      price.product_type === PriceProductType.BARS &&
      derivePaymentType(price) === paymentType,
  )
  const pricePerBox = variant?.unit_amount ?? 0
  return { pricePerBox, pricePerBar: pricePerBox / GUT_HEALTH_BAR_CONFIG.barsPerBox }
}

/**
 * Builds both modes' prices in one pass. Depends only on `prices` — the shared
 * input for the per-mode UI rows and for `buildPDPPricingSummary`.
 */
export const buildBarsPricesByPaymentType = (prices: Price[], currency: CurrencyCode): BarsPricesByPaymentType => ({
  ONE_TIME: buildBarsPrices(prices, 'ONE_TIME', currency),
  '1_MONTH': buildBarsPrices(prices, '1_MONTH', currency),
})

/**
 * Total boxes selected across all flavours.
 */
export const getTotalBoxes = (quantities: Record<GutHealthBarFlavour, number>): number =>
  GUT_HEALTH_BAR_CONFIG.flavours.reduce((sum, flavour) => sum + Math.max(0, quantities[flavour]), 0)

/**
 * Shipping rule: the postage fee applies until the items subtotal reaches the
 * free-delivery threshold (£40). At or above it, delivery is free. Countries
 * without a shipping rule never charge postage.
 */
export const isShippingApplied = (itemsSubtotal: number, country: CheckoutCountry): boolean => {
  const { shipping } = getGutHealthBarCountryConfig(country)
  return !!shipping && itemsSubtotal < shipping.freeShippingThreshold
}

export const getShippingFee = (prices: Price[], country: CheckoutCountry): number => {
  const { shipping } = getGutHealthBarCountryConfig(country)
  if (!shipping) return 0
  const feePrice = prices.find((price) => price.lookup_key === shipping.feeLookupKey)
  return feePrice?.unit_amount ?? shipping.defaultFee
}

export const computeSubtotal = (
  prices: Price[],
  quantities: Record<GutHealthBarFlavour, number>,
  paymentType: GutHealthBarPaymentType,
  currency: CurrencyCode,
): number => {
  const { pricePerBox } = buildBarsPrices(prices, paymentType, currency)
  return pricePerBox * getTotalBoxes(quantities)
}

/**
 * Composes both modes for the PDP, where one-off and subscription cards
 * render side-by-side. Returns totals + savings; per-flavour display values
 * live on `buildBarsPricesByPaymentType`.
 */
export const buildPDPPricingSummary = (
  prices: Price[],
  quantities: Record<GutHealthBarFlavour, number>,
  currency: CurrencyCode,
): PDPPricingSummary => {
  const oneTimeSubtotal = computeSubtotal(prices, quantities, 'ONE_TIME', currency)
  const subscriptionSubtotal = computeSubtotal(prices, quantities, '1_MONTH', currency)

  // With nothing selected the subtotals are 0, so fall back to the per-box
  // (single-box) price difference between the one-off and subscription modes.
  const subtotalSavings = Math.abs(oneTimeSubtotal - subscriptionSubtotal)
  const perBoxSavings = Math.abs(
    buildBarsPrices(prices, 'ONE_TIME', currency).pricePerBox -
      buildBarsPrices(prices, '1_MONTH', currency).pricePerBox,
  )

  return {
    oneTimeSubtotal,
    subscriptionSubtotal,
    savings: subtotalSavings || perBoxSavings,
    totalBars: getTotalBoxes(quantities) * GUT_HEALTH_BAR_CONFIG.barsPerBox,
  }
}
